/** @module global/useMenu */
import { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../components/authentication/AuthContext';
import { AppContext } from './AppContext';
import SimpleCache from './Cache';
import { getMenu } from './request/restaurant';

const cache = new SimpleCache();

/**
 * Hook that returns [reloadFn, menuData, categoriesData, menuSource].
 * It caches results by a key of "restaurantId_isDelivery".
 */
function useMenu() {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuSource, setMenuSource] = useState('standard');

  const cacheKey = appCtx.restaurant ? `${appCtx.restaurant.id}_${appCtx.isDelivery}` : null;

  const reload = useCallback(async () => {
    if (!appCtx.restaurant || !authCtx.tokens) return;

    const menuData = await getMenu(appCtx.restaurant.id, authCtx.tokens.idToken);

    setMenuSource('standard');
    setMenu(menuData);

    // Process standard menu format
    if (Array.isArray(menuData)) {
      const derived = new Set();
      const ranksMap = {};
      menuData.forEach(item => {
        derived.add(item.category);
        ranksMap[item.category] = item.categoryRank;
      });
      const catArr = Array.from(derived);
      const sortedArr = [...catArr].sort(
        (catA, catB) => (ranksMap[catA] || Number.MAX_SAFE_INTEGER) - (ranksMap[catB] || Number.MAX_SAFE_INTEGER),
      );

      setCategories(sortedArr);
      cache.store(cacheKey, {
        menu: menuData,
        categories: catArr,
        menuSource: 'standard',
      });
    }
  }, [appCtx.restaurant, authCtx.tokens, cacheKey]);

  useEffect(() => {
    if (!cacheKey) return;

    if (!cache.has(cacheKey)) {
      reload();
    } else {
      const data = cache.retrieve(cacheKey);
      setMenu(data.menu);
      setCategories(data.categories);
      setMenuSource(data.menuSource || 'standard'); // Default to 'standard' for backward compatibility
    }
  }, [cacheKey, appCtx.restaurant, appCtx.isDelivery, reload]);

  return [reload, menu, categories, menuSource];
}

export default useMenu;
export { cache };
